import React, { useRef } from 'react';

import { FileText, Upload, X } from 'lucide-react';

import { isStatementImage } from '../../utils/statementUploadFormats';

import Button from './Button';
import Typography from './Typography';

export interface FileUploadDropzoneProps {
	title?: string;
	description?: string;
	buttonText?: string;
	accept?: string;
	maxSize?: number; // in MB
	onFileSelect: (file: File) => void;
	/** When set, the input allows several files and all of them are passed here. */
	onFilesSelect?: (files: File[]) => void;
	selectedFiles?: File[];
	onFileRemove?: () => void;
	selectedFile?: File | null;
	dragActive?: boolean;
	onDragEnter?: (e: React.DragEvent) => void;
	onDragLeave?: (e: React.DragEvent) => void;
	onDragOver?: (e: React.DragEvent) => void;
	onDrop?: (e: React.DragEvent) => void;
	disabled?: boolean;
	error?: string;
	className?: string;
	containerClassName?: string;
	buttonClassName?: string;
	buttonTextClassName?: string;
	iconClassName?: string;
	titleClassName?: string;
	descriptionClassName?: string;
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
	title = 'Drag and drop file here',
	description = 'PDF files only, max 10MB',
	buttonText = 'Choose File',
	accept = '.pdf',
	maxSize = 10,
	onFileSelect,
	onFilesSelect,
	selectedFiles,
	onFileRemove,
	selectedFile,
	dragActive = false,
	onDragEnter,
	onDragLeave,
	onDragOver,
	onDrop,
	disabled = false,
	error,
	className = '',
	containerClassName = '',
	buttonClassName = '',
	buttonTextClassName = '',
	iconClassName = '',
	titleClassName = '',
	descriptionClassName = '',
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || !e.target.files[0]) return;
		if (onFilesSelect) {
			onFilesSelect(Array.from(e.target.files));
		} else {
			onFileSelect(e.target.files[0]);
		}
		e.target.value = '';
	};
	const extraFiles =
		selectedFiles && selectedFiles.length > 1 ? selectedFiles.length - 1 : 0;

	const handleChooseFile = () => {
		fileInputRef.current?.click();
	};

	const formatFileSize = (bytes: number) => {
		return (bytes / 1024 / 1024).toFixed(2);
	};

	return (
		<div className={`w-full ${className}`}>
			{!selectedFile ? (
				<div
					className={`
						self-stretch flex-1 min-h-[200px] p-5 bg-neutral-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-300
						flex flex-col justify-center items-center gap-5 transition-colors
						${dragActive ? 'bg-primary-50 outline-primary-300' : ''}
						${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
						${containerClassName}
					`
						.trim()
						.replace(/\s+/g, ' ')}
					onDragEnter={onDragEnter}
					onDragLeave={onDragLeave}
					onDragOver={onDragOver}
					onDrop={onDrop}
					onClick={handleChooseFile}
				>
					<div className="self-stretch flex flex-col justify-center items-center gap-2.5">
						<Upload
							className={`w-[52px] h-[53px] text-[#ACB1B7] ${iconClassName}`.trim()}
						/>
						<div className="self-stretch flex flex-col justify-center items-center gap-[5px]">
							<Typography
								variant="body1"
								className={`text-center text-black text-base font-medium leading-none ${titleClassName}`.trim()}
							>
								{title}
							</Typography>
							<Typography
								variant="caption"
								className={`text-center text-zinc-500 text-xs leading-3 ${descriptionClassName}`.trim()}
							>
								{description}
							</Typography>
						</div>
					</div>

					<Button
						variant="ghost"
						size="sm"
						onClick={(e: React.MouseEvent) => {
							e.stopPropagation();
							handleChooseFile();
						}}
						disabled={disabled}
						className={`w-48 h-10 px-3 py-2 bg-gray-100 rounded-md hover:bg-gray-200 ${buttonClassName}`
							.trim()
							.replace(/\s+/g, ' ')}
					>
						<Typography
							variant="body2"
							className={`text-indigo-700 font-medium ${buttonTextClassName}`
								.trim()
								.replace(/\s+/g, ' ')}
						>
							{buttonText}
						</Typography>
					</Button>

					<input
						ref={fileInputRef}
						type="file"
						className="hidden"
						accept={accept}
						multiple={Boolean(onFilesSelect)}
						onChange={handleFileInput}
						disabled={disabled}
					/>
				</div>
			) : (
				<div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
					<div className="flex items-center justify-between">
						<div className="flex items-center min-w-0 flex-1">
							<FileText className="h-8 w-8 text-red-600 mr-3 flex-shrink-0" />
							<div className="min-w-0 flex-1">
								<Typography
									variant="body2"
									className="font-medium text-gray-900 truncate"
									title={selectedFile.name}
								>
									{selectedFile.name}
								</Typography>
								<Typography variant="caption" className="text-gray-500">
									{extraFiles > 0
										? `${selectedFiles!.length} ${selectedFiles!.every(isStatementImage) ? 'photos' : 'files'}, combined into one statement in this order`
										: `${formatFileSize(selectedFile.size)} MB`}
								</Typography>
								{extraFiles > 0 && (
									<ol className="mt-2 list-decimal space-y-0.5 pl-5 text-xs text-gray-600">
										{selectedFiles!.map((photo) => (
											<li
												key={`${photo.name}-${photo.size}`}
												className="truncate"
												title={photo.name}
											>
												{photo.name} ({formatFileSize(photo.size)} MB)
											</li>
										))}
									</ol>
								)}
							</div>
						</div>
						{onFileRemove && (
							<Button
								variant="ghost"
								size="sm"
								onClick={onFileRemove}
								disabled={disabled}
								className="text-gray-400 hover:text-red-500 p-1"
							>
								<X className="h-5 w-5" />
							</Button>
						)}
					</div>
				</div>
			)}

			{error && (
				<Typography
					variant="caption"
					className="mt-2 text-xs font-medium text-error-600"
				>
					{error}
				</Typography>
			)}
		</div>
	);
};

export default FileUploadDropzone;
